import { useEffect, useMemo, useRef, useState } from 'react';

import { Add, FilterAltOutlined } from '@mui/icons-material';
import { Box, Button, Typography } from '@mui/material';
import { useFilterStore } from 'app/store';
import { useForm } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { useGetMethods } from 'widgets/methods';

import { FilterModal } from 'features/filter';
import { WithdrawalModal } from 'features/withdrawal';
import { useGetWithdrawal } from 'features/withdrawal/api/useGetWithdrawal';
import { useGetWithdrawalExport } from 'features/withdrawal/api/useGetWithdrawalExport';

import { CloseIcon, DownloadCloudIcon, HeaderArrow } from 'shared/assets/icons';
import { ROLES } from 'shared/constants';
import { useGetDate, useUser } from 'shared/hooks';
import { useCustomNavigate } from 'shared/hooks/useCustomNavigate';
import { useUrlParams } from 'shared/hooks/useUrlParams';
import { DateRange, TFiltersWithdrawal } from 'shared/services/types/common.types';
import { TWithdrawal } from 'shared/services/types/withdrawal.types';
import { Layout } from 'shared/ui/layout';
import { MethodSelect } from 'shared/ui/method-select';
import { RoleAccessWrapper } from 'shared/ui/role-access-wrapper';
import { addMessageToast, downloadExportFile } from 'shared/utils';

import { WithdrawalTables } from './slices/TableWithdrawal';

export const Withdrawal = () => {
  const methods = useForm({ mode: 'onBlur' });
  const { filter, setFilter } = useFilterStore();
  const { method_id } = useParams();
  const { customNavigate } = useCustomNavigate();
  const { methods: methodsList } = useGetMethods();
  const { rolesAccess, user } = useUser();
  const { setParam } = useUrlParams();
  const { getDate } = useGetDate();
  const downloadWithdrawalDataTrigger = useRef(false);

  const [isFilterOpen, setIsFilterOpen] = useState(false);
  const [isOpenWithdrawal, setIsOpenWithdrawal] = useState(false);
  const [filterData, setFilterData] = useState<Record<string, any> | null>(null);
  const [pageSize, setPageSize] = useState(20);
  const [accountNumberFrom, setAccountNumberFrom] = useState<string>();
  const [accountNumberTo, setAccountNumberTo] = useState<string>();
  const [subagentNumber, setSubagentNumber] = useState<string>();
  const [amount, setAmount] = useState<number>();
  const [dateFilter, setDateFilter] = useState<DateRange>([null, null]);

  const methodID = parseInt(method_id!);
  const { reset } = methods;

  const activeFilterItems = Object.entries(filter).filter(
    ([key, item]) => item?.toString().length && key !== 'date_to'
  ).length;

  const onSubmitFilters = (data: TFiltersWithdrawal) => {
    //TODO
    setAccountNumberFrom(data.accountNumberFrom?.join(','));
    setAccountNumberTo(data.accountNumberTo?.toString());
    setSubagentNumber(data.subagent_id);
    setAmount(parseInt(data.amount));
    setDateFilter(data.dateRange);
    setFilterData(data);
    setParam('page', '1');
    setIsFilterOpen(false);
    rolesAccess(ROLES.Method.canExportData) &&
      addMessageToast(
        'The filter has been applied, now you can download data with the filter applied',
        'success'
      );
  };

  useEffect(() => {
    setFilter({
      date_from: getDate(0, dateFilter),
      subagent_id: subagentNumber,
      account_id_to: accountNumberTo,
      account_id_from: accountNumberFrom,
      amount: amount
    });
  }, [amount, accountNumberFrom, accountNumberTo, subagentNumber, dateFilter, filterData]);

  const onClose = (isReset?: boolean) => {
    if (isReset) {
      setFilterData(filterData);
    }
    reset();
    setIsFilterOpen(false);
  };

  const { paramsObj } = useUrlParams();
  const page = paramsObj.page ? parseInt(paramsObj.page) - 1 : 0;

  const filterParams = {
    method_id: method_id,
    ...filter
  } as TWithdrawal.getWithdrawalExportData;

  const {
    withdrawal,
    refetchWithdrawal,
    errorMessage: errorWithdrawal
  } = useGetWithdrawalExport(filterParams);

  const handleDownloadData = () => {
    refetchWithdrawal();
    downloadWithdrawalDataTrigger.current = true;
  };

  const onResetFilters = () => {
    setFilter({});
    setFilterData(null);

    addMessageToast('The filter has been reseted', 'success');
  };

  useEffect(() => {
    if (errorWithdrawal) {
      addMessageToast(errorWithdrawal?.detail);
    }
  }, [errorWithdrawal]);

  useEffect(() => {
    if (withdrawal && downloadWithdrawalDataTrigger.current) {
      downloadExportFile(withdrawal, 'withdrawal');

      downloadWithdrawalDataTrigger.current = false;
    }
  }, [withdrawal]);

  const filterW = useMemo(() => {
    return {
      size: pageSize,
      page: page,
      method_id: methodID,
      amount: amount,
      subagent_id: subagentNumber,
      account_id_from: accountNumberFrom,
      account_id_to: accountNumberTo,
      date_from: getDate(0, dateFilter),
      date_to: getDate(1, dateFilter),
      order_by: 'amount',
      is_desc_sort: false
    } as TWithdrawal.GetWithdrawals;
  }, [onSubmitFilters, page, pageSize]);

  const { isWithdrawalLoading, withdrawalData } = useGetWithdrawal(filterW);

  return (
    <Layout bankName='Withdrawal' isSingleMethod>
      <Box
        gridColumn='2'
        gridRow='1'
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          flexWrap: 'wrap',
          gap: 10,
          py: 7,
          px: 12,
          borderRadius: 4,
          bgcolor: 'var(--mui-palette-background-paper)',
          '.MuiButton-root': {
            padding: ' 10px 28px 10px 24px',
            minWidth: 'fit-content',
            maxHeight: 44,
            fontSize: 16,
            fontWeight: 500,
            letterSpacing: '0.25px',
            '&.MuiButton-contained': {
              fontSize: '14px',
              fontWeight: '400',
              py: '12px'
            },
            '&.MuiButton-outlined': {
              fontSize: '14px',
              lineHeight: '20px'
            }
          }
        }}>
        <Box
          sx={{
            flexGrow: 1,
            display: 'flex',
            flexDirection: 'row',
            alignItems: 'center',
            gap: '14px'
          }}>
          <Box
            sx={{
              display: 'flex',
              flexDirection: 'row',
              justifyContent: 'start',
              alignItems: 'center',
              gap: '10px',
              cursor: rolesAccess(ROLES.Methods.canRead) ? 'pointer' : 'default'
            }}
            onClick={() => {
              if (rolesAccess(ROLES.Methods.canRead)) customNavigate(-1);
            }}>
            <Box sx={{ height: '44px' }}>
              <HeaderArrow
                style={{
                  marginTop: '11.5px',
                  color: 'var(--mui-palette-primary-main)'
                }}
              />
            </Box>
            <Typography
              variant='h3'
              color='primary'
              sx={{ fontSize: 28, lineHeight: '36px', fontWeight: '500' }}>
              Withdrawal
            </Typography>
          </Box>
          <Box>
            <MethodSelect
              methodsList={methodsList!}
              useParamsMethodID={method_id!}
              onChange={e => {
                customNavigate(`/method/${e.target.value}/withdrawal`);
              }}
            />
          </Box>
        </Box>
        <RoleAccessWrapper accessCondition={ROLES.Withdrawals.canCreate} currentRole={user?.role}>
          <Button
            variant='contained'
            onClick={() => {
              setIsOpenWithdrawal(true);
            }}
            startIcon={<Add />}>
            Add withdrawal
          </Button>
        </RoleAccessWrapper>
        <Button
          variant='outlined'
          disabled={isWithdrawalLoading}
          sx={{
            position: 'relative'
          }}
          startIcon={<DownloadCloudIcon />}
          onClick={handleDownloadData}>
          Download data
        </Button>
        <Box sx={{ position: 'relative' }}>
          <Button
            variant='outlined'
            type='button'
            onClick={() => {
              setIsFilterOpen(true);
            }}
            sx={{
              '&.MuiButton-root.MuiButton-outlined': {
                paddingRight: activeFilterItems ? '56px' : '28px'
              }
            }}
            key={activeFilterItems}
            startIcon={<FilterAltOutlined />}>
            Filter
          </Button>
          {!!activeFilterItems && (
            <Button
              color='secondary'
              variant='contained'
              type='button'
              onClick={onResetFilters}
              sx={{
                position: 'absolute',
                top: '14px',
                right: '28px',
                width: 16,
                height: 16,
                fontSize: '16px',
                marginLeft: '12px',
                '&.MuiButton-root.MuiButton-contained': {
                  p: '0 !important',
                  border: 'none'
                },
                '&:hover': { color: 'var(--mui-palette-primary-diffs-hover)' }
              }}>
              <CloseIcon />
            </Button>
          )}
        </Box>
      </Box>
      <WithdrawalTables
        size={pageSize}
        setSize={setPageSize}
        withdrawalData={withdrawalData}
        isWithdrawalLoading={isWithdrawalLoading}
      />
      <FilterModal
        key={(!filterData).toString()}
        methodID={methodID}
        onSubmit={data => {
          if ('amount' in data) {
            onSubmitFilters(data);
          }
        }}
        onClose={onClose}
        isFilterOpen={isFilterOpen}
        setIsFilterOpen={setIsFilterOpen}
        userData={filterData}
        methods={methods}
        currentPage='withdrawal'
      />

      <WithdrawalModal isOpen={isOpenWithdrawal} setIsOpen={setIsOpenWithdrawal} />
    </Layout>
  );
};
